import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Box, Card, CardMedia, CardContent, Typography, CircularProgress, LinearProgress, IconButton } from '@mui/material';
import { ImageNotSupported, Close } from '@mui/icons-material';
import { UploadingPhoto } from '../types';

interface UploadingPhotoItemProps {
  uploadingPhoto: UploadingPhoto;
  index: number;
  movePhoto: (dragIndex: number, hoverIndex: number) => void;
  onRemove?: (photoId: string) => void;
}

const DRAG_TYPE = 'PHOTO_ITEM';

interface DragItem {
  photoId: string;
  index: number;
  isUploading?: boolean;
}

const UploadingPhotoItem: React.FC<UploadingPhotoItemProps> = ({ uploadingPhoto, index, movePhoto, onRemove }) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const [{ isDragging }, dragRef] = useDrag({
    type: DRAG_TYPE,
    item: { photoId: uploadingPhoto.id, index, isUploading: true },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: false, // Disable dragging for uploading photos
  });

  const [, dropRef] = useDrop({
    accept: DRAG_TYPE,
    hover: (item: DragItem, monitor) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      movePhoto(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  // Combine drag and drop refs
  dragRef(dropRef(ref));

  const renderOverlay = () => {
    switch (uploadingPhoto.status) {
      case 'uploading':
        return (
          <Box sx={styles.uploadingOverlay}>
            <CircularProgress variant='determinate' value={uploadingPhoto.progress} size={60} sx={styles.progress} />
            <Typography variant='body2' sx={styles.overlayText}>
              {uploadingPhoto.progress}%
            </Typography>
            <Typography variant='caption'>{uploadingPhoto.file.name}</Typography>
          </Box>
        );
      case 'converting':
        return (
          <Box sx={styles.convertingOverlay}>
            <CircularProgress size={60} sx={styles.progress} />
            <Typography variant='body2' sx={styles.overlayText}>
              Converting HEIC...
            </Typography>
            <Typography variant='caption'>Please wait</Typography>
          </Box>
        );
      case 'error':
        return (
          <Box sx={styles.errorOverlay}>
            <Typography variant='body2' sx={styles.overlayText}>
              Upload Failed
            </Typography>
            <Typography variant='caption'>{uploadingPhoto.error || 'Unknown error'}</Typography>
            {onRemove && (
              <IconButton onClick={() => onRemove(uploadingPhoto.id)} sx={styles.removeButton} size='small'>
                <Close />
              </IconButton>
            )}
          </Box>
        );
      case 'success':
        return <Box sx={styles.successBadge}>✓</Box>;
      default:
        return null;
    }
  };

  const renderCaptionContent = () => {
    switch (uploadingPhoto.status) {
      case 'converting':
        return (
          <Box>
            <Typography variant='body2' color='text.secondary' sx={styles.captionText}>
              Converting HEIC to JPEG...
            </Typography>
            <Box sx={styles.progressContainer}>
              <LinearProgress sx={styles.linearProgress} />
            </Box>
            <Typography variant='caption' color='text.secondary'>
              Please wait while converting...
            </Typography>
          </Box>
        );
      case 'uploading':
        return (
          <Box>
            <Typography variant='body2' color='text.secondary' sx={styles.captionText}>
              Uploading...
            </Typography>
            <Box sx={styles.progressContainer}>
              <LinearProgress variant='determinate' value={uploadingPhoto.progress} sx={styles.linearProgress} />
            </Box>
            <Typography variant='caption' color='text.secondary'>
              {uploadingPhoto.progress}% complete
            </Typography>
          </Box>
        );
      case 'success':
        return (
          <Typography variant='body2' color='text.secondary' sx={styles.successText}>
            Fetching...
          </Typography>
        );
      case 'error':
        return (
          <Box>
            <Typography variant='body2' color='error.main' sx={styles.errorText}>
              Upload failed
            </Typography>
            <Typography variant='caption' color='error.main'>
              {uploadingPhoto.error || 'Unknown error'}
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      ref={ref}
      sx={{
        ...styles.container,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <Box sx={styles.wrapper}>
        <Card sx={styles.card}>
          {/* Photo Preview */}
          {uploadingPhoto.status === 'converting' ? (
            <Box sx={styles.convertingPlaceholder}>
              <Box sx={styles.placeholderIcon}>
                <ImageNotSupported fontSize='large' />
              </Box>
            </Box>
          ) : (
            <CardMedia
              component='img'
              height='300'
              image={uploadingPhoto.preview}
              alt={uploadingPhoto.file.name}
              sx={{
                ...styles.cardMedia,
                opacity: uploadingPhoto.status === 'uploading' ? 0.7 : 1,
              }}
            />
          )}

          {/* Status Overlay */}
          {renderOverlay()}
        </Card>

        {/* Caption Area */}
        <CardContent sx={styles.cardContent}>{renderCaptionContent()}</CardContent>
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    cursor: 'default', // No grab cursor since dragging is disabled
  },
  wrapper: {
    position: 'relative' as const,
  },
  card: {
    position: 'relative' as const,
    cursor: 'default',
    border: '1px solid',
    borderColor: 'divider',
    transition: 'all 0.2s ease-in-out',
    overflow: 'hidden',
    '&:hover': {
      boxShadow: 4,
      transform: 'translateY(-2px)',
    },
  },
  cardMedia: {
    objectFit: 'cover' as const,
    backgroundColor: 'grey.100',
    margin: 0,
    padding: 0,
    borderRadius: 0,
  },
  convertingPlaceholder: {
    height: 300,
    backgroundColor: 'grey.300',
    position: 'relative' as const,
  },
  placeholderIcon: {
    position: 'absolute' as const,
    top: 8,
    left: 8,
    color: 'grey.600',
  },
  uploadingOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white !important',
  },
  convertingOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(25, 118, 210, 0.3)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
  },
  errorOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(244, 67, 54, 0.8)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    p: 2,
  },
  successBadge: {
    position: 'absolute' as const,
    top: 8,
    right: 8,
    backgroundColor: 'success.main',
    color: 'white',
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progress: {
    color: 'white',
    mb: 1,
  },
  overlayText: {
    fontWeight: 'bold',
  },
  cardContent: {
    p: '0.5rem 1.5rem !important',
  },
  captionText: {
    mb: 1,
  },
  progressContainer: {
    width: '100%',
    mb: 1,
  },
  linearProgress: {
    height: 6,
    borderRadius: 3,
  },
  successText: {
    fontStyle: 'italic',
  },
  errorText: {
    fontStyle: 'italic',
  },
  removeButton: {
    position: 'absolute' as const,
    top: 8,
    right: 8,
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
};

export default UploadingPhotoItem;
